import { AccordionDetails, Button, Grid, IconButton, Rating, Typography } from '@mui/material';
import { Add, Delete, Edit } from '@mui/icons-material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StorefrontReviewData } from '../../../api';
import { Accordion, AccordionSummary } from '../../../calendar/dialogs/EditOrderDialog/components/Accordion';
import { useLanguage } from '../../../common/GeneralUtils';
import { StorefrontPanels } from '../StorefrontDrawer';
import { ReviewEditDialog } from './ReviewEditDialog';

type ReviewsPanelProps = {
  reviews: StorefrontReviewData[];
  onChange: (reviews: StorefrontReviewData[]) => void;
  expanded?: StorefrontPanels;
  onPanelChange: (panel: StorefrontPanels) => (event: React.SyntheticEvent, isExpanded: boolean) => void;
  panelName: StorefrontPanels;
};

export const ReviewsPanel = ({ reviews, onChange, expanded, onPanelChange, panelName }: ReviewsPanelProps) => {
  const { t } = useTranslation();
  const language = useLanguage();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const handleAdd = () => {
    setEditingIndex(null);
    setDialogOpen(true);
  };

  const handleEdit = (index: number) => {
    setEditingIndex(index);
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setEditingIndex(null);
  };

  const handleSave = (review: StorefrontReviewData) => {
    if (editingIndex === null) {
      onChange([...reviews, review]);
    } else {
      const updated = [...reviews];
      updated[editingIndex] = review;
      onChange(updated);
    }
  };

  const handleDelete = (index: number) => {
    onChange(reviews.filter((_, i) => i !== index));
  };

  const editingReview = editingIndex !== null ? reviews[editingIndex] : null;

  return (
    <>
      <Accordion expandedPanelName={expanded} panelName={panelName} onChange={onPanelChange}>
        <AccordionSummary
          title={t('storefronts.reviews')}
          summaryComponent={<Typography variant="body2">{reviews.length}</Typography>}
        />
        <AccordionDetails>
          <Grid container flexDirection="column" gap={2}>
            {reviews.map((review, index) => (
              <Grid
                key={index}
                container
                alignItems="center"
                justifyContent="space-between"
                sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: '6px' }}>
                <Grid item xs={8}>
                  <Typography fontWeight="600">
                    {review.reviewer_name?.[language] || review.reviewer_name?.en || review.reviewer_name?.he}
                  </Typography>
                  <Rating value={review.score} precision={0.5} size="small" readOnly />
                  {(review.text?.[language] || review.text?.en || review.text?.he) && (
                    <Typography variant="body2" color="text.secondary">
                      {review.text?.[language] || review.text?.en || review.text?.he}
                    </Typography>
                  )}
                </Grid>
                <Grid item>
                  <IconButton size="small" onClick={() => handleEdit(index)}>
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDelete(index)}>
                    <Delete fontSize="small" />
                  </IconButton>
                </Grid>
              </Grid>
            ))}

            {reviews.length === 0 && (
              <Typography color="text.secondary" variant="body2">
                {t('storefronts.no_reviews')}
              </Typography>
            )}

            <Button startIcon={<Add />} onClick={handleAdd}>
              {t('storefronts.add_review')}
            </Button>
          </Grid>
        </AccordionDetails>
      </Accordion>

      <ReviewEditDialog open={dialogOpen} onClose={handleClose} onSave={handleSave} review={editingReview} />
    </>
  );
};
